import { Box, Menu } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import type { ReactNode, MouseEvent, CSSProperties } from 'react';
import { useState, useRef, useEffect, useId } from 'react';
import { useBottomMenuContext } from './BottomMenuContext';

const Root = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  padding: theme.spacing(0.5),
  borderRadius: theme.shape.borderRadius * 2,
  cursor: 'pointer',
  position: 'relative',

  '&:hover .bottom-item-icon': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
}));

const IconArea = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ theme, active }) => ({
  width: 32,
  height: 32,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  position: 'relative',
  borderRadius: theme.shape.borderRadius * 2,
  color: active ? '#063FFE' : '#7e828f',
  transition: 'background-color 150ms ease, color 150ms ease',
}));

const pingPong = keyframes`
  0%, 20%  { transform: translateX(0); }
  50%, 70% { transform: translateX(var(--label-overflow, 0px)); }
  100%     { transform: translateX(0); }
`;

const LabelWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'hasOverflow',
})<{ hasOverflow?: boolean }>(({ hasOverflow }) => ({
  overflow: 'hidden',
  width: '100%',
  position: 'relative',

  ...(hasOverflow && {
    '&:not(:hover)::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      width: 10,
      background:
        'linear-gradient(to right, rgba(255,255,255,0), rgba(255,255,255,0.85))',
      pointerEvents: 'none',
    },
  }),
}));

const LabelInner = styled('span')<{ animate: boolean; active: boolean }>(
  ({ animate, active, theme }) => ({
    display: 'block',
    fontSize: 10,
    fontWeight: active ? 500 : 400,
    lineHeight: 'normal',
    color: active ? theme.palette.primary.main : '#7e828f',
    whiteSpace: 'nowrap',
    textAlign: animate ? 'left' : 'center',
    transition: 'color 150ms ease',
    ...(animate && {
      animation: `${pingPong} 3.6s ease-in-out infinite`,
    }),
  }),
);

interface Props {
  icon: ReactNode;
  label: string;
  menuContent: ReactNode[];
}

export function BottomItem({ icon, label, menuContent }: Props) {
  const id = useId();
  const { anchorData, setAnchorData } = useBottomMenuContext();
  const isOpen = anchorData?.id === id;
  const anchorEl = isOpen && anchorData ? anchorData.el : null;

  const [isHovered, setIsHovered] = useState(false);
  const [overflowAmount, setOverflowAmount] = useState(0);
  const labelRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (labelRef.current) {
      const el = labelRef.current;
      setOverflowAmount(el.scrollWidth - el.offsetWidth);
    }
  }, [label]);

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    if (menuContent.length === 0) {
      return;
    }

    event.stopPropagation();
    setAnchorData({ id, el: event.currentTarget });
  };

  const handleClose = () => {
    setAnchorData(null);
  };

  const shouldAnimate = isHovered && overflowAmount > 0;

  return (
    <>
      <Root
        onClick={handleOpen}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <IconArea className="bottom-item-icon" active={isOpen}>
          {icon}
        </IconArea>
        <LabelWrapper hasOverflow={overflowAmount > 0}>
          <LabelInner
            ref={labelRef}
            animate={shouldAnimate}
            active={isOpen}
            style={
              { '--label-overflow': `-${overflowAmount}px` } as CSSProperties
            }
          >
            {label}
          </LabelInner>
        </LabelWrapper>
      </Root>
      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        onClick={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              p: 1,
              ml: 1,
              minWidth: 200,
              boxShadow: '0px 0px 8px 0px rgba(0, 0, 0, 0.25)',
              borderRadius: 4,
            },
          },
        }}
      >
        {menuContent}
      </Menu>
    </>
  );
}
